import { EntityKind, GROUND_OWNED_MAX, GroundState, groundYours, type EntityState } from "odyssey-shared";

/**
 * The page's own ground items (P2.8-T65, ADR-091's amendment for P2.8-T13, decisions 3 and 6): the page learns which items
 * are its own from the record, which a server of PROTOCOL 6.60.0 or later sends in state 2 for the character the page plays
 * (state `GroundState.OwnedByYou`, ADR-091's amendment for P2.8-T81), so an item reserved for it before a reload or a rejoin is its own at
 * its first record, and from `lootGained`'s `grounded` handles, which it keeps here, for a server before that minor and for
 * the moment between a drop's message and its first record. A mark holds only while its item is owned: it ends when the item turns public, when a
 * handle seen as a ground item leaves the view or is reused by another kind, and when the session ends. A handle the
 * view has not shown yet keeps its mark, since `lootGained` can arrive before the snapshot that spawns its item. A
 * character owns at most `GROUND_OWNED_MAX` items, so past that the oldest mark goes. After a reconnect the page holds no
 * mark and reads its own items from their records; the server answers every pick-up all the same.
 */
export class GroundMarks {
  private readonly seen = new Map<number, boolean>();

  /**
   * Marks the handles a `lootGained` put on the ground for the page's character.
   *
   * @param grounded The message's `grounded`: each handle, item and quantity.
   */
  public mark(grounded: readonly (readonly [handle: number, item: string, quantity: number])[]): void {
    for (const [handle] of grounded) {
      this.seen.delete(handle);
      this.seen.set(handle, false);
      if (this.seen.size > GROUND_OWNED_MAX) {
        const oldest = this.seen.keys().next().value;
        if (oldest !== undefined) {
          this.seen.delete(oldest);
        }
      }
    }
  }

  /**
   * Whether a handle is one of the page's own items, still owned.
   *
   * @param handle The handle.
   * @returns True while it is marked.
   */
  public has(handle: number): boolean {
    return this.seen.has(handle);
  }

  /**
   * Whether a ground item's record is one of the page's own, still owned: the server sent it in state `OwnedByYou` for this
   * page's character (`groundYours`), or it is in state `Owned` and `lootGained` named its handle. An item turned public is
   * no one's, whatever a mark says, and a state the page does not know is another's.
   *
   * @param state The item's record.
   * @returns True while the item is the page's own.
   */
  public owns(state: EntityState): boolean {
    return groundYours(state) || (state.anim === GroundState.Owned && this.seen.has(state.handle));
  }

  /**
   * How many marks are held.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.seen.size;
  }

  /**
   * Ends the marks the view no longer bears out, after a snapshot is applied: an item turned public, a handle seen as
   * a ground item that has left, and a handle another kind now holds.
   *
   * @param entities The client's view.
   * @returns Whether any mark ended.
   */
  public settle(entities: ReadonlyMap<number, EntityState>): boolean {
    let ended = false;
    for (const [handle, seen] of this.seen) {
      const state = entities.get(handle);
      if (state === undefined) {
        if (seen) {
          this.seen.delete(handle);
          ended = true;
        }
      } else if (state.kind !== EntityKind.GroundItem || (state.anim !== GroundState.Owned && state.anim !== GroundState.OwnedByYou)) {
        this.seen.delete(handle);
        ended = true;
      } else if (!seen) {
        this.seen.set(handle, true);
      }
    }
    return ended;
  }

  /**
   * Forgets every mark, as a session ends.
   */
  public clear(): void {
    this.seen.clear();
  }
}
